import type {
  CreateTeamMemberRequest,
  PendingInvite,
  TeamMember,
  TeamMemberFilters,
  TeamStats,
  UpdateTeamMemberRequest,
} from '../types/team.types'

class TeamService {
  private teamMembers: TeamMember[] = []

  constructor() {
    this.teamMembers = this.generateMockTeamMembers()
  }

  private delay(ms: number = 200): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  private generateMockTeamMembers(): TeamMember[] {
    const firstNames = [
      'John',
      'Jane',
      'Mike',
      'Sarah',
      'David',
      'Lisa',
      'Robert',
      'Emily',
      'Michael',
      'Jessica',
      'Chris',
      'Amanda',
      'Daniel',
      'Ashley',
      'Matthew',
      'Jennifer',
      'William',
      'Linda',
      'James',
      'Barbara',
    ]
    const lastNames = [
      'Smith',
      'Johnson',
      'Williams',
      'Brown',
      'Jones',
      'Garcia',
      'Miller',
      'Davis',
      'Rodriguez',
      'Martinez',
      'Hernandez',
      'Lopez',
      'Wilson',
      'Anderson',
      'Thomas',
      'Taylor',
      'Moore',
      'Jackson',
      'Martin',
      'Lee',
    ]
    const domains = [
      'company.com',
      'organization.org',
      'business.net',
      'enterprise.io',
    ]

    const members: TeamMember[] = []

    // Always include the current user as admin
    members.push({
      id: 'member-001',
      email: 'you@company.com',
      name: 'You',
      role: 'admin',
      status: 'active',
      joined_at: '2024-01-15T10:00:00Z',
      last_active: new Date().toISOString(),
      invited_by: 'system',
    })

    // Generate additional team members
    for (let i = 2; i <= 12; i++) {
      const firstName =
        firstNames[Math.floor(Math.random() * firstNames.length)]
      const lastName = lastNames[Math.floor(Math.random() * lastNames.length)]
      const domain = domains[Math.floor(Math.random() * domains.length)]

      const roles: TeamMember['role'][] = ['admin', 'member', 'viewer']
      const role = roles[Math.floor(Math.random() * roles.length)]

      // More realistic status distribution
      let status: TeamMember['status'] = 'active'
      if (Math.random() < 0.15) status = 'pending'
      if (Math.random() < 0.05) status = 'inactive'

      const joinedDate = new Date(
        Date.now() - Math.floor(Math.random() * 365) * 24 * 60 * 60 * 1000
      )
      const lastActiveDate =
        status === 'active'
          ? new Date(
              Date.now() - Math.floor(Math.random() * 7) * 24 * 60 * 60 * 1000
            )
          : undefined

      members.push({
        id: `member-${String(i).padStart(3, '0')}`,
        email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@${domain}`,
        name: `${firstName} ${lastName}`,
        role: role,
        status: status,
        joined_at: joinedDate.toISOString(),
        last_active: lastActiveDate?.toISOString(),
        invited_by: members[Math.floor(Math.random() * (i - 1))].id,
      })
    }

    return members
  }

  async getTeamMembers(filters?: TeamMemberFilters): Promise<TeamMember[]> {
    await this.delay()

    let filteredMembers = [...this.teamMembers]

    if (filters) {
      if (filters.role) {
        filteredMembers = filteredMembers.filter(
          member => member.role === filters.role
        )
      }

      if (filters.status) {
        filteredMembers = filteredMembers.filter(
          member => member.status === filters.status
        )
      }

      if (filters.search) {
        const search = filters.search.toLowerCase()
        filteredMembers = filteredMembers.filter(
          member =>
            member.name.toLowerCase().includes(search) ||
            member.email.toLowerCase().includes(search)
        )
      }
    }

    return filteredMembers
  }

  async inviteTeamMember(data: CreateTeamMemberRequest): Promise<TeamMember> {
    await this.delay(800) // Simulate email sending delay

    const newMember: TeamMember = {
      id: `member-${Date.now()}`,
      email: data.email,
      name: data.name || data.email.split('@')[0],
      role: data.role,
      status: 'pending',
      joined_at: new Date().toISOString(),
      invited_by: 'member-001', // Current user
    }

    this.teamMembers.unshift(newMember)
    return newMember
  }

  async updateTeamMember(
    memberId: string,
    data: UpdateTeamMemberRequest
  ): Promise<TeamMember> {
    await this.delay()

    const memberIndex = this.teamMembers.findIndex(
      member => member.id === memberId
    )
    if (memberIndex === -1) {
      throw new Error('Team member not found')
    }

    const updatedMember = {
      ...this.teamMembers[memberIndex],
      ...data,
    }

    this.teamMembers[memberIndex] = updatedMember
    return updatedMember
  }

  async removeTeamMember(memberId: string): Promise<void> {
    await this.delay()

    const memberIndex = this.teamMembers.findIndex(
      member => member.id === memberId
    )
    if (memberIndex === -1) {
      throw new Error('Team member not found')
    }

    this.teamMembers.splice(memberIndex, 1)
  }

  async resendInvite(memberId: string): Promise<void> {
    await this.delay(600)

    const member = this.teamMembers.find(member => member.id === memberId)
    if (!member) {
      throw new Error('Team member not found')
    }

    if (member.status !== 'pending') {
      throw new Error('Can only resend invites to pending members')
    }

    // In a real app, this would trigger email resending
  }

  async getTeamStats(): Promise<TeamStats> {
    await this.delay()

    const activeMembers = this.teamMembers.filter(
      member => member.status === 'active'
    )
    const pendingInvites = this.teamMembers.filter(
      member => member.status === 'pending'
    )
    const adminCount = this.teamMembers.filter(
      member => member.role === 'admin'
    )

    return {
      total_members: this.teamMembers.length,
      active_members: activeMembers.length,
      pending_invites: pendingInvites.length,
      admin_count: adminCount.length,
    }
  }

  async getPendingInvites(): Promise<PendingInvite[]> {
    await this.delay()

    const pendingMembers = this.teamMembers.filter(
      member => member.status === 'pending'
    )

    return pendingMembers.map(member => {
      const invitedBy = this.teamMembers.find(m => m.id === member.invited_by)

      return {
        id: member.id,
        email: member.email,
        name:
          member.name !== member.email.split('@')[0] ? member.name : undefined,
        role: member.role as 'admin' | 'member' | 'viewer',
        created_by: member.invited_by || 'system',
        created_by_name: invitedBy?.name || 'System',
        created_at: member.joined_at,
        invite_link: `https://app.company.com/invite/${member.id}`,
      }
    })
  }

  async getActiveTeamMembers(): Promise<TeamMember[]> {
    await this.delay()
    return this.teamMembers.filter(member => member.status !== 'pending')
  }
}

export const teamService = new TeamService()
